import axios from "axios";
import { useState } from "react";
import { Link, redirect, useLoaderData, useNavigate } from "react-router-dom";
import { Header } from "../layout/header.js";
import { Footer } from "../layout/footer.js";
import { LearnCardList } from "../components/learncardlist.js";
import { RegionPicker } from "../components/regionpicker.js";
import { SearchBar } from "../components/searchbar.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library as fontAwesomeLibrary } from "@fortawesome/fontawesome-svg-core";
import {
	faUpRightFromSquare,
	faCircleInfo,
	faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import landingPageBg from "../images/landingpage-bg2.jpg";
import landingPageImg from "../images/landing-page-img.jpg";
import hdsSkrimImg from "../images/hds-skrim.png";
import { staticCardListContent } from "../staticdata.js";
import { faBlackTie } from "@fortawesome/free-brands-svg-icons";

/**
 * React-router-dom Loader function to load backend endpoint with
 * AJAX, the response from which drives the page.
 *
 * @param params Parameters of the page URL
 */
export async function loader({ params }) {
	try {
		const response = await axios.get(`/heliodata/app/load_landing_page`);
		return { landingPageData: response.data };
	} catch (error) {
		return redirect("/error");
	}
}

/**
 * React component to display the landing page.
 */
export function LandingPageContent() {
	const { landingPageData } = useLoaderData();
	const { learnItems, datasetCount } = landingPageData;
	const navigate = useNavigate();
	const [currentQuery, setCurrentQuery] = useState("");
	const [hideInfoToast, setHideInfoToast] = useState(false);

	const closeToast = () => {
            setHideInfoToast(true);
	};

        const getAllDatasets = async () => {
            try {
                const aph = "*";
                const url = `/search/${aph}/1`;
                navigate(url);
            } catch (err) {
                if (err.response?.status === 404) {
                    console.error("Datasets not found (404)");
                } else {
                    console.error("Error fetching datasets:", err);
                }
                navigate("/error");
            };
        };

	fontAwesomeLibrary.add(faUpRightFromSquare, faArrowRight);

	return (
		<div className="landingPageCanvas">
			<div className="landingPageMainContainer">
				<Header wide={false} scrollToTop={false} hideSearch={true} />
				{!hideInfoToast &&
					<div className={`toastContainer`}>
						<div className="toast">
							<div className="toastTextContainer">
								<FontAwesomeIcon
									icon={faCircleInfo}
									className="toastInfoIcon" />

								{/*<button className="toastLearnMore" onClick={handleLearnMore}>Learn More ></button>*/}
							</div>
							<div className="toastText">This is a beta site</div>
							<button className="toastClose" onClick={closeToast}>&times;</button>
						</div>
					</div>}

				<div className="landingPageBanner" style={{ backgroundImage: `url(${landingPageBg})` }}>
					<div className="bannerContainer">
						<div className="landingPageBannerTitle">
							Data Discovery for the <br></br>
							Heliophysics Community
						</div>
						<div className="landingPageBannerSubtitle">
							Empowering data access and analysis through open science
						</div>

						<div className="landingPageBannerDescription"></div>

						<div className="landingPageBannerNotice">
							{/*Data from NASA in-situ missions are currently available. Additions of solar imagery data and NOAA missions are happening right now.*/}
						</div>
					</div>

					<div className="SearchTitle">Search For Data</div>

					<div className="landingPageSearchWrapper">
						<div className="SearchContent">
							<SearchBar
								centered={true}
								currentQuery={currentQuery}
								setCurrentQuery={setCurrentQuery}
								navigate={navigate}
								placeholder={
									window.innerWidth <= 600
										? 'Enter text to search (e.g., PSP)'
										: 'Enter text to search (e.g., solar wind, Parker Solar Probe, MMS FPI)'
								}
							/>
							<div className="landingPageDatasetCountContainer">
                                                          <span role="button" className="landingPageDatasetCount" onClick={getAllDatasets}>
                                                            {datasetCount} datasets
                                                          </span>
                                                        </div>

							<div className="SearchParagraphTitle">
								HelioData Provides Pathways to Heliophysics Data
							</div>
							<div className="SearchParagraphWrapper">
								<div className="SearchParagraphLeft">
									This tool offers open access to thousands of datasets from
									current and historic NASA Heliophysics missions and a selection
									of heliophysics data from other U.S. and international agencies. Heliophysics
									studies the relationship between the Sun, planets, and the space
									environment as a dynamic and interconnected system.
								</div>
								<div className="SearchParagraphRight">
									This data supports heliophysics research and critical space
									weather investigations. Browse datasets, find links to services
									to download data in various formats, and explore common software
									analysis tools.
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="landingPageContainer">

					<div className="landingPageRegionPickerContainer">
						<RegionPicker />
					</div>

					<div className="landingPageBasicsContainer">
						<div className="landingPageBasicsSection">
							<div className="archivalAccessCard">
								<div className="archivalAccessGrid">
									<div className="archivalAccessTextCol">
										<span className="archivalAccessEyebrow">65+ Years &middot; 2+ Petabytes &middot; 600+ Instruments</span>
										<h2 className="archivalAccessTitle">Archival Access</h2>
										<p className="archivalAccessBody">
											HelioData's archive includes over 2 petabytes of big data spanning more than 65 years of space exploration. This includes datasets from more than 600 instruments on more than 100 missions and observatories. Featured missions include <Link to="/mission/ParkerSolarProbe">Parker Solar Probe</Link>, the first spacecraft to fly through the solar corona, and historic NASA missions such as <Link to="/mission/Pioneer10">Pioneer 10</Link>, the first spacecraft placed on a trajectory to interstellar space and the first to collect data beyond Neptune.
										</p>
									</div>
									<div className="archivalAccessNavCol">
										<span className="archivalAccessEyebrow">Learn More</span>
										<div className="waypointList">
											<Link to="/downloading-and-acquiring" className="waypointLink">
												<button
													title="Step-by-Step User Guide"
													className="waypointButton"
												>
													<span className="waypointDot waypointDot--red" aria-hidden="true"></span>
													<span className="waypointContent">
														<span className="waypointTitle">Step-by-Step User Guide</span>
														<span className="waypointMeta">Get Started</span>
													</span>
													<span className="waypointIcon" aria-hidden="true">
														<FontAwesomeIcon icon={faArrowRight} />
													</span>
												</button>
											</Link>

											<Link to="/missionlist" className="waypointLink">
												<button
													title="Missions & Observatories"
													className="waypointButton"
												>
													<span className="waypointDot waypointDot--cyan" aria-hidden="true"></span>
													<span className="waypointContent">
														<span className="waypointTitle">Missions & Observatories</span>
														<span className="waypointMeta">Profiles and Datasets</span>
													</span>
													<span className="waypointIcon" aria-hidden="true">
														<FontAwesomeIcon icon={faArrowRight} />
													</span>
												</button>
											</Link>

											<a href="/where-it-comes-from" className="waypointLink">
												<button
													title="Data Origins"
													className="waypointButton"
												>
													<span className="waypointDot waypointDot--white" aria-hidden="true"></span>
													<span className="waypointContent">
														<span className="waypointTitle">Data Origins</span>
														<span className="waypointMeta">Instrumentation and Types of Data</span>
													</span>
													<span className="waypointIcon" aria-hidden="true">
														<FontAwesomeIcon icon={faArrowRight} />
													</span>
												</button>
											</a>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="landingPageLearnContainer">
						<div className="landingPageLearnConstraint">
							<div className="landingPageInfoBar">
								<div className="landingPageLearnAboutCommunity">
									<div className="LandingPageTitle"><h2>Visualize Data</h2><h3>Explore Heliophysics Through Data Visualization</h3></div>

								</div>
							</div>
							<div>
								<div className="landingPageInfoBarCardList">
									<LearnCardList learnItems={staticCardListContent} />
								</div>
							</div>
						</div>
					</div>
				</div>
				<Footer />
			</div>
		</div>
	);
}
